// src/data/testTerritory.js - Record 613 (Bob 10/8). F22, THE TESTERS' TEST TERRITORY (Bob 2026-10-08: "Agreed, help me
// set that up"). A tester (role 'tester', committee GO-342 - data\YCP_USER_ROLES.csv) holds the scope
// 'TEST|GO-342|SANDBOX', written into his token by scripts\set-roles.js from the CSV's scopes column. With it the
// discipline New Case screen works for him: firestore.rules (testerSandbox()) treats him as that territory's Local
// Chairperson ONLY for that scopeKey and ONLY on a case marked isExample == true - the EXAMPLE ONLY mark (ISS-46 46.7).
// Every case a tester writes is stamped here (exampleStamp) and shows EXAMPLE ONLY wherever it is drawn. An example never
// names a real member and never appears in a real officer's list (keepForViewer; src\data\disciplineQueries.js) - and no
// real Local Chairperson holds a TEST| key (set-roles.js refuses one on an LC row).
//
// THE NUMBER AND THE LADDER ARE THE SERVER'S (Record 611, F20). A tester's case is written as a DRAFT like every case;
// the disciplineAct function opens it and numbers it EXAMPLE-<committee>-<case id> (src\data\disciplineLadder.js
// exampleCaseNumberFor()) - never from counters/discipline_<committee>_<year>. Who may act on an example is may() in
// disciplineLadder.js (testerOnExample()); actsAsLcOn() below reads the same test so the screen and the function agree.
//
// PURE. Proved by scripts\test-test-territory.mjs; the rules by scripts\test-firestore-rules.mjs (R613 F22).
import { TEST_SCOPE as LADDER_TEST_SCOPE, isSandboxExample } from './disciplineLadder';

export const TEST_SCOPE = LADDER_TEST_SCOPE;   // 'TEST|GO-342|SANDBOX' - one definition, in the ladder
export const EXAMPLE_MARK = 'EXAMPLE ONLY';

// The committee a TEST| key belongs to ('TEST|GO-342|SANDBOX' -> 'GO-342'); '' for any other key.
export function testScopeCommittee(key) {
  const m = /^TEST\|(GO-\d+)\|SANDBOX$/.exec(String(key || ''));
  return m ? m[1] : '';
}

export function isTesterAccount(user) {
  return !!user && user.role === 'tester' && !user.isAdmin;
}

export function holdsTestTerritory(user) {
  return isTesterAccount(user) && Array.isArray(user.scopes) && user.scopes.indexOf(TEST_SCOPE) !== -1
    && user.committee === testScopeCommittee(TEST_SCOPE);
}

// The EXAMPLE ONLY mark, for display and for keeping examples out of real lists. (The rules bind the mark to the
// territory, so a marked case is always in it; the ladder's isSandboxExample() requires both.)
export function isExampleCase(kase) {
  return !!kase && kase.isExample === true;
}

// The fields every case a tester writes carries - or why it cannot be written. A real account gets nothing added.
//   { ok: true, stamp: {} }                                   not a tester
//   { ok: true, stamp: { isExample, scopeKey, generalCommittee, memberEmail } }   a tester holding the territory
//   { ok: false, why }                                        a tester whose token does not hold it yet
export function exampleStamp(user) {
  if (!isTesterAccount(user)) return { ok: true, stamp: {} };
  const committee = testScopeCommittee(TEST_SCOPE);
  if (!holdsTestTerritory(user)) {
    return { ok: false, why: 'This is a test account, and its sign-in does not carry the test territory ' + TEST_SCOPE
      + (user.committee && user.committee !== committee ? ' (it carries committee ' + user.committee + ', not ' + committee + ')' : '')
      + ' yet. Nothing was written. The administrator runs scripts\\set-roles.js --confirm; then sign out and sign in again.' };
  }
  // An example names nobody but the tester himself: the e-mail is his own, so no real member is ever made a party to it.
  return { ok: true, stamp: { isExample: true, scopeKey: TEST_SCOPE, generalCommittee: committee, memberEmail: String(user.email || '') } };
}

// Record 662 (Bob 2026-10-12: "yes" - keep testers' claims in the test sandbox, like their discipline cases). Greg's tester
// login filed a real claim (YCP-GO342-2026-0014) into the real Local LC1951-342C and took the next real number. A claim a
// tester writes now carries the same stamp: EXAMPLE ONLY, in the test territory, never in a real officer's list
// (claimQueries.js), numbered EXAMPLE-... by nextClaimNumber (functions\index.js), and held there by firestore.rules
// (claims create / coreIntact). Applied by claimWrite.js claimPatch() to every full claim write (one carrying scopeKey).
export function claimExampleStamp(user) {
  if (!isTesterAccount(user)) return { ok: true, stamp: {} };
  const committee = testScopeCommittee(TEST_SCOPE);
  if (!holdsTestTerritory(user)) return exampleStamp(user);   // the same refusal, word for word
  return { ok: true, stamp: { isExample: true, scopeKey: TEST_SCOPE, committee, generalCommittee: committee } };
}

// Does this tester work this case as its Local Chairperson would (the rules' testerSandbox() arm; the ladder's
// testerOnExample())?
export function actsAsLcOn(user, kase) {
  return holdsTestTerritory(user) && isSandboxExample(kase) && String((kase && kase.generalCommittee) || '') === user.committee;
}

// Bob 2026-10-12 ("4. yes"): a tester works his EXAMPLE claims as their Local Chairperson would, to test the officer's side.
// The claim page reads the signed-in person through this: on a sandbox example the tester is shown the Local Chairperson's
// controls (role 'local_chair'); everywhere else he is exactly who he is. tokenRole keeps his real role for any record that
// must match the token (firestore.rules officerNotes: role == token role). The rules decide (lcEdit's testerSandbox arm).
export function viewerFor(user, claim) {
  // Not on his own draft: a draft is its author's alone (ruling 56), and the rules' lcEdit refuses a draft for everyone.
  if (!user || !isTesterAccount(user) || !actsAsLcOn(user, claim) || String((claim && claim.status) || '') === 'draft') return user;
  return { ...user, role: 'local_chair', tokenRole: user.role, testerAsLC: true, scopes: Array.from(new Set([...(user.scopes || []), TEST_SCOPE])) };
}

// The rows a viewer's LIST shows: a tester sees his examples; nobody else ever sees one in a list.
export function keepForViewer(user, rows) {
  const list = Array.isArray(rows) ? rows : [];
  return isTesterAccount(user) ? list : list.filter(r => !isExampleCase(r));
}
